import { Bell } from "lucide-react";
import * as React from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogBody, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Spinner } from "@/components/ui/feedback";
import { Tooltip } from "@/components/ui/misc";
import { Notification } from "@/components/ui/notification";
import { Popover, PopoverAnchor, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useToast } from "@/components/ui/toast";
import { useAuth } from "@/lib/auth";
import {
  bellBadge, freshNotifications, KIND_CHAT_MENTION, notificationView, notThere, targetPath, titleWithCount,
  useNotifications, useReadNotifications, type UserNotification,
} from "@/lib/notifications";
import { railOffset } from "@/lib/phone-page";
import { useNow } from "@/lib/use-now";
import { cn } from "@/lib/utils";
import { RAIL_CELL } from "@/softphone/rail";
import { PHONE_COMPANION } from "@/softphone/voicemail-suggestion";

/**
 * The bell (ACL-506, ADR-161 and ADR-162 in `platform`): what the console tells the person without
 * being asked, behind a standing button with a count of what they have not seen. Reminders before
 * their appointments and callbacks, a callback the dialer gave to somebody else, a lead's email in
 * a thread they read (ACL-379, platform ADR-199), and any kind core-api learns to say: every one is
 * drawn by `Notification` from the one shape it comes in, so a new kind needs nothing here.
 *
 * On a desktop-width window it is a button of the phone's rail, under the missed calls, built as
 * `MissedCallsRailButton` is: the rail is where a rep brings things up from. A person with no phone
 * (and an admin signed in as somebody, whose session has none, ADR-040) gets the rail with the bell
 * alone (`BellRail`). A narrow window has no rail, so there it is in the header, beside the menu.
 *
 * The popover shows the newest five, each with its details folded until it is pressed; Show more
 * gives way to a drawer with every one (ACL-594).
 *
 * Opening the list is reading it: what it shows is marked seen on the server (`POST
 * /notifications/read`), so the count goes in every tab, and the rows that were new stay lit while
 * the list is open. An admin signed in as somebody reads it without marking it: core-api answers as
 * if it had, and this asks nothing.
 *
 * One that comes while the console is open is toasted too (`NotificationWatcher`), and the count is
 * in the tab's title.
 */

/** The popover's box, as wide as the missed calls': a notification is a few lines. */
const CONTENT =
  "flex w-96 max-w-[calc(100vw-2rem)] flex-col p-0 max-h-[var(--radix-popover-content-available-height)]";

/** How many the popover shows (ACL-594); Show more opens every one in a drawer. */
const PANEL_ROWS = 5;

const NONE: readonly UserNotification[] = [];

/**
 * The list itself, newest first, each row with its details folded until it is pressed (ACL-594).
 * `fresh` are the rows that were new when it was opened.
 */
function NotificationsList({
  fresh, onNavigate, limit,
}: {
  fresh: ReadonlySet<number>;
  /** The person followed a row: the popover or drawer around the list closes itself. */
  onNavigate: () => void;
  /** Only the newest this many. */
  limit?: number;
}) {
  const list = useNotifications();
  const now = useNow(30_000);
  if (list.isPending) {
    return <div className="flex justify-center py-6"><Spinner /></div>;
  }
  if (list.error) {
    return <p className="px-4 py-3 text-xs text-text-muted">Could not load your notifications.</p>;
  }
  const items = list.data?.items ?? NONE;
  if (items.length === 0) {
    return (
      <p className="px-4 py-3 text-xs text-text-muted">
        Nothing yet. A reminder shows up here 15 and 5 minutes before each of your appointments and
        callbacks, and a lead&apos;s email as soon as it arrives.
      </p>
    );
  }
  return (
    <ul className="divide-y divide-border">
      {items.slice(0, limit).map((n) => (
        <Notification
          key={n.id}
          as="li"
          {...notificationView(n, now)}
          foldDetails
          unread={fresh.has(n.id)}
          onNavigate={onNavigate}
        />
      ))}
    </ul>
  );
}

/**
 * Open and close, the count, and marking seen, for the bell wherever it is. While the list is
 * open, in the popover or the drawer, every unread notification on it is marked, once each, so one
 * that arrives while it is open is read too; the rows that were new when it opened stay lit until
 * it closes.
 */
function useBell() {
  const { user } = useAuth();
  const signedInAs = Boolean(user?.signed_in_by);
  const list = useNotifications();
  const read = useReadNotifications();
  const [open, setOpen] = React.useState(false);
  const [drawerOpen, setDrawerOpen] = React.useState(false);
  const reading = open || drawerOpen;
  const [fresh, setFresh] = React.useState<ReadonlySet<number>>(new Set());
  const sent = React.useRef(new Set<number>());
  const items = list.data?.items ?? NONE;
  const unreadIds = items.filter((n) => n.read_at === null).map((n) => n.id);
  const unreadKey = unreadIds.join(",");
  // For `toggle`, which stays the same function from render to render.
  const unreadNow = React.useRef(unreadIds);
  unreadNow.current = unreadIds;

  const toggle = React.useCallback((next: boolean) => {
    if (next) {
      setFresh(new Set(unreadNow.current));
      // A mark that failed last time is asked for again.
      sent.current.clear();
    }
    setOpen(next);
  }, []);

  /** Show more: the popover gives way to the drawer, its new rows still lit. */
  const showAll = React.useCallback(() => {
    setOpen(false);
    setDrawerOpen(true);
  }, []);

  /** The popover and the drawer both: the person is done with the bell. */
  const close = React.useCallback(() => {
    setOpen(false);
    setDrawerOpen(false);
  }, []);

  const { mutate } = read;
  React.useEffect(() => {
    if (!reading || signedInAs) return;
    const ids = unreadKey ? unreadKey.split(",").map(Number).filter((id) => !sent.current.has(id)) : [];
    if (ids.length === 0) return;
    for (const id of ids) sent.current.add(id);
    setFresh((prev) => new Set([...prev, ...ids]));
    mutate(ids);
  }, [reading, signedInAs, unreadKey, mutate]);

  const unread = reading ? 0 : (list.data?.unread ?? 0);
  return {
    open,
    toggle,
    drawerOpen,
    showAll,
    close,
    /** Every notification the console has: the newest 50, as core-api lists them. */
    total: list.data?.items.length ?? 0,
    fresh,
    badge: bellBadge(unread),
    name: unread > 0 ? `Notifications, ${unread} new` : "Notifications",
    /** core-api has no notifications yet: no bell at all. */
    missing: notThere(list.error),
  };
}

function BellIcon({ badge }: { badge: string | null }) {
  return (
    <>
      <Bell className="size-[18px]" aria-hidden />
      {badge && (
        <span
          className="absolute -right-1.5 -top-1.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-danger-9 px-1 text-2xs font-semibold leading-none text-white ring-2 ring-frame tabular"
          aria-hidden
        >
          {badge}
        </span>
      )}
    </>
  );
}

type BellState = ReturnType<typeof useBell>;

/** What the popover holds: the newest few, and past them Show more. */
function BellPanel({ bell }: { bell: BellState }) {
  return (
    <>
      <div className="shrink-0 border-b border-border px-4 py-3">
        <h2 className="text-sm font-semibold text-text">Notifications</h2>
        <p className="text-xs text-text-muted">Your reminders, and what changed, newest first.</p>
      </div>
      <div className="min-h-0 overflow-y-auto scrollbar-subtle">
        <NotificationsList fresh={bell.fresh} limit={PANEL_ROWS} onNavigate={bell.close} />
      </div>
      {bell.total > PANEL_ROWS && (
        <div className="flex shrink-0 items-center justify-between gap-3 border-t border-border px-4 py-2.5">
          <span className="text-xs tabular text-text-muted">
            Showing {PANEL_ROWS} of {bell.total}
          </span>
          <Button size="sm" onClick={bell.showAll}>
            Show more
          </Button>
        </div>
      )}
    </>
  );
}

/**
 * Every notification, in a drawer the window's height (ACL-594): what Show more opens in place of
 * the popover. Closing it is done with the bell, so nothing is left open behind it.
 */
function NotificationsDrawer({
  bell, companion,
}: {
  bell: BellState;
  /** The phone's own marks, so opening this in the rail does not close the phone. */
  companion?: Record<string, string>;
}) {
  return (
    <Dialog open={bell.drawerOpen} onOpenChange={(next) => !next && bell.close()}>
      <DialogContent variant="drawer" size="sm" {...companion}>
        <DialogHeader>
          <DialogTitle>Notifications</DialogTitle>
          <DialogDescription>Your reminders, and what changed, newest first.</DialogDescription>
        </DialogHeader>
        <DialogBody className="p-0">
          <NotificationsList fresh={bell.fresh} onNavigate={bell.close} />
        </DialogBody>
      </DialogContent>
    </Dialog>
  );
}

/**
 * The bell in the phone's rail, under the missed calls. Its own popover root and the companion
 * mark, as the rail's other lists have, so a press here does not close the phone; out beside what
 * the rail has open (`railOffset`); out of the way by itself when a call rings.
 */
export function NotificationsRailButton({
  phoneOpen = false, suggestionOpen = false, ringing = false,
}: {
  /** The phone's own popover is open. */
  phoneOpen?: boolean;
  /** A voicemail is suggested and its choices are open. */
  suggestionOpen?: boolean;
  /** A call is ringing this phone. */
  ringing?: boolean;
}) {
  const bell = useBell();
  const [tipOpen, setTipOpen] = React.useState(false);
  const { close } = bell;
  // The drawer too: it covers the rail, and the call is answered from there.
  React.useEffect(() => {
    if (ringing || suggestionOpen) close();
  }, [ringing, suggestionOpen, close]);
  if (bell.missing) return null;

  const companion = { [PHONE_COMPANION]: "" };
  return (
    <>
      <Popover open={bell.open} onOpenChange={bell.toggle}>
        <PopoverAnchor asChild>
          <div className={RAIL_CELL} {...companion}>
            <Tooltip content={bell.name} side="left" open={tipOpen && !bell.open} onOpenChange={setTipOpen}>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  // Radix opens the tooltip from a focus handler that stands down for a prevented event.
                  onFocus={(e) => e.preventDefault()}
                  aria-label={bell.name}
                  className={cn(
                    "relative flex size-9 items-center justify-center rounded-full transition-colors",
                    bell.open ? "bg-frame-hover text-text" : "text-text-muted hover:bg-frame-hover hover:text-text",
                  )}
                >
                  <BellIcon badge={bell.badge} />
                </button>
              </PopoverTrigger>
            </Tooltip>
          </div>
        </PopoverAnchor>
        <PopoverContent
          side="left"
          align="start"
          sideOffset={railOffset({ phoneOpen, suggestionOpen })}
          aria-label="Notifications"
          className={CONTENT}
          {...companion}
        >
          <BellPanel bell={bell} />
        </PopoverContent>
      </Popover>
      <NotificationsDrawer bell={bell} companion={companion} />
    </>
  );
}

/** The bell in the narrow window's header, beside the menu: there is no rail there. */
export function NotificationsHeaderButton() {
  const bell = useBell();
  if (bell.missing) return null;
  return (
    <>
      <Popover open={bell.open} onOpenChange={bell.toggle}>
        <PopoverTrigger asChild>
          <button
            type="button"
            aria-label={bell.name}
            className={cn(
              "relative flex size-8 items-center justify-center rounded-full transition-colors",
              bell.open ? "bg-surface-hover text-text" : "text-text-muted hover:bg-surface-hover hover:text-text",
            )}
          >
            <BellIcon badge={bell.badge} />
          </button>
        </PopoverTrigger>
        <PopoverContent side="bottom" align="end" aria-label="Notifications" className={CONTENT}>
          <BellPanel bell={bell} />
        </PopoverContent>
      </Popover>
      <NotificationsDrawer bell={bell} />
    </>
  );
}

/**
 * Renders nothing: toasts each notification that comes while the console is open, with a button
 * to open it, and keeps the count in front of the tab's title. The first list it reads is what was
 * already there, so a page loaded with some waiting toasts none of them; the bell has them.
 */
export function NotificationWatcher() {
  const list = useNotifications();
  const toast = useToast();
  const navigate = useNavigate();
  const known = React.useRef<Set<number> | null>(null);
  const items = list.data?.items;

  React.useEffect(() => {
    if (!items) return;
    if (known.current === null) {
      known.current = new Set(items.map((n) => n.id));
      return;
    }
    for (const n of freshNotifications(items, known.current)) {
      // A mention in a chat (platform ADR-186) pops from the chat's own toast, which opens it beside
      // the rail; the bell keeps it in its list.
      if (n.kind === KIND_CHAT_MENTION) continue;
      const href = targetPath(n.target);
      toast.notify(n.title, n.message ?? undefined, href ? { label: "Open", onSelect: () => navigate(href) } : undefined);
    }
    for (const n of items) known.current.add(n.id);
  }, [items, toast, navigate]);

  const unread = list.data?.unread ?? 0;
  React.useEffect(() => {
    document.title = titleWithCount(document.title, unread);
  }, [unread]);
  // Signed out: the title without a count.
  React.useEffect(() => () => void (document.title = titleWithCount(document.title, 0)), []);
  return null;
}
